import {
  sanitizeCoordination,
  type ClashTriagePatch,
} from './clashTriage'

// ---------------------------------------------------------------------------
// Proposta de triagem importada de um ficheiro JSON.
//
// Quem prepara a coordenacao (uma pessoa, uma folha de calculo, outro
// programa) pode entregar a triagem ja feita: prioridade, responsavel, a
// quem comunicar, recomendacao e etiquetas, por conflito ou por grupo de
// conflitos repetidos. O painel casa cada entrada com os conflitos da
// corrida pelos GlobalId dos dois elementos, por qualquer ordem: os
// localId nao servem, porque mudam a cada carregamento, e a ordem A/B
// depende de que conjunto e A.
//
// O formato esta documentado em docs/CLASH.md ("Proposta de triagem").
// Le-se com tolerancia: campos desconhecidos ignoram-se, um campo mal
// escrito fica de fora e diz-se, e uma entrada sem os dois GlobalId nao
// estraga as outras.
// ---------------------------------------------------------------------------

export const FORMATO_PROPOSTA = 'vn2r-clash-proposta'
export const VERSAO_PROPOSTA = 1

export interface PropostaConflito {
  /** GlobalId dos dois elementos, pela ordem em que vieram. */
  guids: [string, string]
  patch: ClashTriagePatch
}

export interface Proposta {
  projecto: string | null
  conflitos: PropostaConflito[]
  /** Entradas recusadas ao ler, com o motivo. */
  avisos: string[]
}

export type LeituraProposta =
  | { ok: true; proposta: Proposta }
  | { ok: false; motivo: string }

type Obj = Record<string, unknown>

function obj(v: unknown): Obj | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : null
}

function guid(v: unknown): string | null {
  if (typeof v !== 'string') return null
  const t = v.trim()
  // GlobalId IFC: 22 caracteres na base 64 do IFC. Aceita-se tambem um
  // GUID com hifens, que algumas ferramentas exportam.
  return t.length >= 16 && t.length <= 64 ? t : null
}

/** Os dois GlobalId de uma entrada: `guids: [a, b]`, ou `A` e `B` (o
 *  formato da analise do Cha139), ou `a` e `b`, ou `guid_a` e `guid_b`. */
function parDeGuids(o: Obj): [string, string] | null {
  const lista = Array.isArray(o.guids) ? o.guids : null
  const a = guid(lista ? lista[0] : (o.A ?? o.a ?? o.guid_a))
  const b = guid(lista ? lista[1] : (o.B ?? o.b ?? o.guid_b))
  if (!a || !b || a === b) return null
  return [a, b]
}

/** Campos da proposta (em portugues, como no ficheiro) para o patch da
 *  triagem. */
function semAcentos(v: string): string {
  return v
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
}

function textos(v: unknown): string[] {
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string')
  return typeof v === 'string' ? [v] : []
}

function camposDaProposta(o: Obj): Obj {
  // Piso e nivel por cota entram nas etiquetas ("Piso 3", "Nível P5"),
  // que e onde as outras plataformas os mostram e filtram.
  const etiquetas = o.etiquetas ?? o.tags
  const extra: string[] = []
  // Um grupo que atravessa varios pisos traz "P1, P2, P3": uma etiqueta
  // por nivel, nao uma com a lista.
  const partes = (v: unknown): string[] =>
    typeof v === 'string' ? v.split(',').map((x) => x.trim()).filter(Boolean) : []
  for (const piso of partes(o.piso)) extra.push(piso)
  for (const nivel of partes(o.nivel)) extra.push(`Nível ${nivel}`)
  // Das etiquetas da analise saem o tipo (ja vai no tipo do topico), os
  // sistemas (dizem-se na descricao e enchiam a lista) e a regra da
  // triagem ("Regra M2"), que e informacao da analise. As etiquetas com a
  // lista de niveis numa so ("Nível P1, P2") tambem.
  const regras = textos(o.regra).flatMap((r) => [r, `Regra ${r}`])
  const fora = new Set(
    [...textos(o.tipo), ...textos(o.sistemas), ...regras].map(semAcentos),
  )
  const tags =
    etiquetas === undefined && extra.length === 0
      ? undefined
      : [...textos(etiquetas), ...extra].filter(
          (t) => !fora.has(semAcentos(t)) && !/^n[ií]vel\b.*,/i.test(t.trim()),
        )
  return {
    priority: o.prioridade ?? o.priority,
    assignedTo: o.responsavel ?? o.assignedTo,
    notify: o.a_comunicar ?? o.comunicar ?? o.notify,
    recommendation: o.recomendacao ?? o.recommendation,
    dueDate: o.prazo ?? o.dueDate,
    tags,
  }
}

/** Uma prioridade entre parenteses ("(fora da contagem)") e um
 *  comentario de quem fez a analise, nao um valor: fica sem prioridade
 *  e sem aviso. */
function eComentario(v: unknown): boolean {
  return typeof v === 'string' && /^\s*\(.*\)\s*$/.test(v)
}

function avisosDosCampos(o: Obj, onde: string, avisos: string[]): ClashTriagePatch {
  const campos = camposDaProposta(o)
  const patch = sanitizeCoordination(campos)
  if (campos.priority !== undefined && patch.priority === undefined && !eComentario(campos.priority)) {
    avisos.push(`${onde}: prioridade "${String(campos.priority)}" não reconhecida.`)
  }
  if (campos.dueDate !== undefined && patch.dueDate === undefined) {
    avisos.push(`${onde}: prazo "${String(campos.dueDate)}" não é uma data AAAA-MM-DD.`)
  }
  return patch
}

/** Lê e valida o JSON da proposta. */
export function lerProposta(raw: unknown): LeituraProposta {
  const raiz = Array.isArray(raw) ? { conflitos: raw } : obj(raw)
  if (!raiz) return { ok: false, motivo: 'O ficheiro não tem uma proposta de triagem.' }
  if (raiz.formato !== undefined && raiz.formato !== FORMATO_PROPOSTA) {
    return {
      ok: false,
      motivo: `Formato "${String(raiz.formato)}" desconhecido. Esperado "${FORMATO_PROPOSTA}".`,
    }
  }
  if (typeof raiz.versao === 'number' && raiz.versao > VERSAO_PROPOSTA) {
    return {
      ok: false,
      motivo: `Proposta na versão ${raiz.versao}; esta aplicação lê até à versão ${VERSAO_PROPOSTA}.`,
    }
  }

  const avisos: string[] = []
  const conflitos: PropostaConflito[] = []
  const grupos = new Map<string, ClashTriagePatch>()

  // Grupos: `grupos` (o formato documentado), e `topicos` e
  // `duplicacao_modelacao` (o formato da analise do Cha139), cada um com
  // os pares em `conflitos` ou em `pares`.
  const lista = (v: unknown): unknown[] => (Array.isArray(v) ? v : [])
  const gruposBrutos = [
    ...lista(raiz.grupos),
    ...lista(raiz.topicos),
    ...lista(raiz.duplicacao_modelacao),
  ]
  gruposBrutos.forEach((g, i) => {
    const go = obj(g)
    if (!go) return
    const id = typeof go.id === 'string' && go.id.trim() ? go.id.trim() : `G${i + 1}`
    const titulo = typeof go.titulo === 'string' ? go.titulo : undefined
    const base: ClashTriagePatch = {
      ...avisosDosCampos(go, `Grupo ${id}`, avisos),
      group: id,
      ...(titulo ? { groupTitle: titulo.trim().slice(0, 200) } : {}),
    }
    grupos.set(id, base)
    const membros = Array.isArray(go.conflitos) ? go.conflitos : lista(go.pares)
    membros.forEach((m, j) => {
      const mo = obj(m) ?? (Array.isArray(m) ? { guids: m } : null)
      const par = mo ? parDeGuids(mo) : null
      if (!mo || !par) {
        avisos.push(`Grupo ${id}, conflito ${j + 1}: faltam os dois GlobalId.`)
        return
      }
      conflitos.push({
        guids: par,
        patch: { ...base, ...avisosDosCampos(mo, `Grupo ${id}, conflito ${j + 1}`, avisos) },
      })
    })
  })

  const soltos = Array.isArray(raiz.conflitos) ? raiz.conflitos : []
  soltos.forEach((c, i) => {
    const co = obj(c)
    const par = co ? parDeGuids(co) : null
    if (!co || !par) {
      avisos.push(`Conflito ${i + 1}: faltam os dois GlobalId.`)
      return
    }
    const idGrupo = typeof co.grupo === 'string' ? co.grupo.trim() : ''
    const base = idGrupo ? (grupos.get(idGrupo) ?? { group: idGrupo }) : {}
    if (idGrupo && !grupos.has(idGrupo)) {
      avisos.push(`Conflito ${i + 1}: o grupo "${idGrupo}" não está na lista de grupos.`)
    }
    conflitos.push({
      guids: par,
      patch: { ...base, ...avisosDosCampos(co, `Conflito ${i + 1}`, avisos) },
    })
  })

  if (conflitos.length === 0) {
    return {
      ok: false,
      motivo:
        avisos.length > 0
          ? `Nenhum conflito legível na proposta. ${avisos[0]}`
          : 'A proposta não tem conflitos.',
    }
  }
  const projecto =
    typeof raiz.projecto === 'string' && raiz.projecto.trim() ? raiz.projecto.trim() : null
  return { ok: true, proposta: { projecto, conflitos, avisos } }
}

/** Chave de um par de GlobalId, sem ordem. */
export function chaveDoPar(a: string, b: string): string {
  return a <= b ? `${a}|${b}` : `${b}|${a}`
}

export interface ConflitoComGuids {
  /** `clashKey` do conflito na corrida. */
  key: string
  guids: [string | null, string | null]
}

export interface AplicacaoProposta {
  /** Patch por `clashKey`, pronto para `applyClashTriageMany`. */
  patches: Record<string, ClashTriagePatch>
  /** Conflitos da corrida que receberam triagem da proposta. */
  aplicados: number
  /** Entradas da proposta sem conflito correspondente nesta corrida. */
  semCorrespondencia: PropostaConflito[]
  /** Conflitos da corrida que a proposta não cobre: ficam por triar. */
  porTriar: number
}

/**
 * Casa a proposta com os conflitos da corrida pelos GlobalId (a ordem
 * A/B é indiferente). Um par de elementos pode dar mais do que um
 * conflito (duro e folga): a proposta aplica-se a todos.
 */
export function casarProposta(
  proposta: Proposta,
  conflitos: readonly ConflitoComGuids[],
): AplicacaoProposta {
  const porPar = new Map<string, ConflitoComGuids[]>()
  for (const c of conflitos) {
    const [a, b] = c.guids
    if (!a || !b) continue
    const k = chaveDoPar(a, b)
    const lista = porPar.get(k)
    if (lista) lista.push(c)
    else porPar.set(k, [c])
  }
  const patches: Record<string, ClashTriagePatch> = {}
  const semCorrespondencia: PropostaConflito[] = []
  for (const entrada of proposta.conflitos) {
    const alvos = porPar.get(chaveDoPar(entrada.guids[0], entrada.guids[1]))
    if (!alvos || alvos.length === 0) {
      semCorrespondencia.push(entrada)
      continue
    }
    for (const alvo of alvos) {
      const [a, b] = alvo.guids
      patches[alvo.key] = {
        ...(patches[alvo.key] ?? {}),
        ...entrada.patch,
        ...(a && b ? { guids: [a, b] as [string, string] } : {}),
      }
    }
  }
  const aplicados = Object.keys(patches).length
  return {
    patches,
    aplicados,
    semCorrespondencia,
    porTriar: conflitos.length - aplicados,
  }
}

/** Frase para o painel depois de importar. */
export function resumoProposta(a: AplicacaoProposta, avisos: number): string {
  const partes = [
    `${a.aplicados} conflito${a.aplicados === 1 ? '' : 's'} com triagem da proposta`,
  ]
  if (a.porTriar > 0) {
    partes.push(`${a.porTriar} da corrida ficam por triar`)
  }
  if (a.semCorrespondencia.length > 0) {
    const n = a.semCorrespondencia.length
    partes.push(
      `${n} entrada${n === 1 ? '' : 's'} da proposta sem conflito correspondente nesta corrida`,
    )
  }
  if (avisos > 0) {
    partes.push(`${avisos} aviso${avisos === 1 ? '' : 's'} na leitura`)
  }
  return `${partes.join('; ')}.`
}
